import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { Download, LogOut } from "lucide-react";
import EventsPanel from "./EventsPanel";

const TABS = [
  { id: "attendance", label: "My Attendance" },
  { id: "marks", label: "My Marks" },
  { id: "fees", label: "Fees" },
  { id: "reports", label: "Report Cards" },
  { id: "events", label: "Events" },
];

export default function ParentPortal() {
  const [tab, setTab] = useState("attendance");
  const [me, setMe] = useState(null);
  const [err, setErr] = useState("");
  const [gate, setGate] = useState({ gated: false });
  useEffect(() => {
    api.get("/parent/me").then(r => setMe(r.data)).catch(() => setErr("not_signed_in"));
    api.get("/parent/gate").then(r => setGate(r.data)).catch(() => {});
  }, []);
  const logout = async () => {
    try { await api.post("/auth/logout", {}); } catch { /* fine */ }
    window.location.href = "/login";
  };
  if (err === "not_signed_in") {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-[#FBF9F6]">
        <div className="nb-card p-6 max-w-md bg-[#FCA5A5]">
          <h1 className="display text-2xl font-bold">Sign in required</h1>
          <p className="mt-2 text-sm">Please open the Parent Portal from your child&apos;s Student Console → <b>Parent Access</b> tab using your parent password (child password + <b>P</b>).</p>
          <a href="/login" className="nb-btn nb-btn-secondary mt-4 inline-flex">Go to Login</a>
        </div>
      </div>
    );
  }
  if (!me) return <div className="min-h-screen grid place-items-center">Loading…</div>;
  const stu = me.student;
  const tabs = TABS.map(t => (t.id === "marks" || t.id === "reports") && gate.gated
    ? { ...t, label: `🔒 ${t.label}`, title: `Locked — clear dues of ₹${gate.outstanding}` }
    : t);
  const gatedLock = (
    <div className="nb-card p-8 bg-[#FCA5A5] space-y-2" data-testid="parent-gated-lock">
      <span className="nb-badge bg-white text-xs">🔒 Locked</span>
      <h2 className="display text-2xl font-bold">Marks & Report Cards are locked</h2>
      <p className="text-sm">Outstanding fees of <b>₹{gate.outstanding}</b> exceed the school's access threshold. Please clear the dues to unlock.</p>
    </div>
  );
  return (
    <Shell title="Parent Portal"
           subtitle={`${stu.name} · ${stu.class_name}-${stu.section}`}
           tabs={tabs} active={tab} onTab={setTab}>
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6">
        <div className="flex justify-end mb-3">
          <button onClick={logout} className="nb-btn nb-btn-ghost text-xs" data-testid="parent-logout">
            <LogOut size={14}/> Sign out
          </button>
        </div>
        {tab === "attendance" && <ParentAttendance/>}
        {tab === "marks" && (gate.gated ? gatedLock : <ParentMarks/>)}
        {tab === "fees" && <ParentFees/>}
        {tab === "reports" && (gate.gated ? gatedLock : <ParentReports/>)}
        {tab === "events" && <EventsPanel mode="view"/>}
      </div>
    </Shell>
  );
}

function ParentAttendance() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/parent/attendance").then(r => setD(r.data)); }, []);
  if (!d) return <div>Loading…</div>;
  return (
    <div className="space-y-4" data-testid="parent-attendance">
      <div className="nb-card p-6 bg-[#86EFAC] flex flex-wrap gap-6">
        <div><div className="nb-label">Present</div><div className="display text-4xl font-black">{d.present}</div></div>
        <div><div className="nb-label">Total Days</div><div className="display text-4xl font-black">{d.total}</div></div>
        <div><div className="nb-label">Percentage</div><div className="display text-4xl font-black">{d.percentage}%</div></div>
      </div>
      <div className="nb-card p-0 overflow-hidden">
        <table className="nb-table">
          <thead><tr><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {d.history.map(h => (
              <tr key={h.date}>
                <td className="mono">{h.date}</td>
                <td>
                  <span className={"nb-badge " + (h.present ? "bg-[#86EFAC]" : "bg-[#FCA5A5]")}>
                    {h.present ? "Present" : "Absent"}
                  </span>
                </td>
              </tr>
            ))}
            {d.history.length === 0 && <tr><td colSpan={2} className="text-center py-6 text-slate-500">No attendance records yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ParentMarks() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/parent/marks").then(r => setRows(r.data)); }, []);
  const grouped = {};
  rows.forEach(m => { (grouped[m.exam] = grouped[m.exam] || []).push(m); });
  return (
    <div className="space-y-4" data-testid="parent-marks">
      {Object.entries(grouped).map(([exam, list]) => (
        <div key={exam} className="nb-card p-0 overflow-hidden">
          <div className="p-4 bg-[#FDE047] border-b-2 border-black display text-lg font-bold">{exam}</div>
          <table className="nb-table">
            <thead><tr><th>Subject</th><th>Marks</th><th>Max</th></tr></thead>
            <tbody>
              {list.map(m => (
                <tr key={`${m.exam}-${m.subject}`}>
                  <td className="font-bold">{m.subject}</td>
                  <td className="mono">{m.marks}</td>
                  <td className="mono">{m.max_marks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {rows.length === 0 && <div className="nb-card p-6 text-slate-500">No marks recorded yet.</div>}
    </div>
  );
}

function ParentFees() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/parent/receipts").then(r => setRows(r.data)); }, []);
  const dl = async (r) => {
    try {
      const res = await api.get(`/fees/receipt/${r.receipt_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${r.receipt_no || r.receipt_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); }
  };
  return (
    <div className="nb-card p-0 overflow-hidden" data-testid="parent-fees">
      <table className="nb-table">
        <thead><tr><th>Receipt</th><th>Date</th><th>Amount</th><th></th></tr></thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.receipt_id}>
              <td className="mono font-bold">
                {r.receipt_no}
                {r.revised && <span className="nb-badge bg-[#FDE047] text-[10px] ml-1">R</span>}
              </td>
              <td className="mono text-xs">{(r.paid_at || "").slice(0, 10)}</td>
              <td className="mono">₹{(r.amount || 0).toLocaleString()}</td>
              <td>
                <button onClick={() => dl(r)} className="nb-btn nb-btn-secondary px-2 py-1 text-xs">
                  <Download size={12}/> PDF
                </button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">No receipts yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ParentReports() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/parent/report-cards").then(r => setRows(r.data)); }, []);
  return (
    <div className="space-y-3" data-testid="parent-reports">
      {rows.map(r => (
        <div key={r.publication_id} className="nb-card p-4 flex items-center gap-3">
          <div className="flex-1">
            <div className="font-bold">{r.test_type} — {r.class_name}-{r.section}</div>
            <div className="text-xs mono">Published {(r.published_at || r.created_at || "").slice(0, 10)} · {(r.marks||[]).length} subjects</div>
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/parent/report-card-pdf/${r.publication_id}`}
             target="_blank" rel="noreferrer"
             className="nb-btn nb-btn-secondary text-xs"
             data-testid={`parent-report-dl-${r.publication_id}`}>
            <Download size={12}/> Download PDF
          </a>
        </div>
      ))}
      {rows.length === 0 && <div className="nb-card p-6 text-slate-500">No published report cards yet.</div>}
    </div>
  );
}
